
// SignageBrokerageOfficeForm.tsx
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { addressLine1Validation, cityValidation, countryValidation, officeNameValidation, phone2Validation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import { Building2 } from "lucide-react";
import { UIInput, UIInputDisable } from "components/Inputfields";
import AddressAutocomplete from "components/AddressAutocomplete";
import { getAuthCookie } from "../../utils/authCookies";
type OfficeAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  _id?: string;
};

type Office = {
  _id: string;
  officeName: string;
  officeType?: string;
  officeAddress?: OfficeAddress[];
};

type DuplicateOfficeCandidate = {
  resource_id: string;
  resource_name: string;
};
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const SignageBrokerageOfficeForm: React.FC = () => {
  const { firmId, officeId } = useParams<{ firmId: string; officeId?: string }>();
  const isEdit = Boolean(officeId);
  const token = getAuthCookie("sessionToken") || "";
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [office, setOffice] = useState<Office | null>(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    candidates: DuplicateOfficeCandidate[];
    message: string;
  } | null>(null);
  const [duplicateChoice, setDuplicateChoice] = useState<"CREATE_NEW" | "USE_EXISTING">("CREATE_NEW");
  const [existingOfficeId, setExistingOfficeId] = useState("");
  const pendingCreateBodyRef = useRef<Record<string, unknown> | null>(null);

  // Load existing office when editing
  useEffect(() => {
    if (!isEdit || !officeId) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/offices/${officeId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        setOffice(data as Office);
      } catch (err) {
        console.error("Error fetching office:", err);
        showError("Failed to load office");
      }
    })();
  }, [isEdit, officeId, token]);

  // Formik
  
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      officeName: office?.officeName || "",
      officeType: office?.officeType || "",
      officePhone: office?.officeAddress?.[0]?.phone || DEFAULT_TEN_ZEROS,
      streetAddress1: office?.officeAddress?.[0]?.addressLine1 || "",
      streetAddress2: office?.officeAddress?.[0]?.addressLine2 || "",
      city: office?.officeAddress?.[0]?.city || "",
      state: office?.officeAddress?.[0]?.state || "",
      zipcode: office?.officeAddress?.[0]?.zip || "",
      country: office?.officeAddress?.[0]?.country || US_COUNTRY_DEFAULTS.country,

      latitude: toCoordinate(office?.officeAddress?.[0]?.latitude),
      longitude: toCoordinate(office?.officeAddress?.[0]?.longitude),
      cityCode: office?.officeAddress?.[0]?.cityCode || "",
      stateCode: office?.officeAddress?.[0]?.stateCode || "",
      countryCode: office?.officeAddress?.[0]?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
    },
    validationSchema: Yup.object({
      officeName: officeNameValidation,
      officePhone: phone2Validation,
      streetAddress1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      country: countryValidation,
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        if (isEdit && officeId) {
          // UPDATE office
          const response = await fetch(`${process.env.REACT_APP_API_URL}/offices/${officeId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              officeName: values.officeName || "",
              officeType: values.officeType || "",
              officeAddress: [
                {
                  addressLine1: values.streetAddress1 || "",
                  addressLine2: values.streetAddress2 || "",
                  city: values.city || "",
                  state: values.state || "",
                  country: addressCountry,
                  zip: values.zipcode || "",
                  phone:withDefault(values.officePhone) || values.officePhone ,
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                  cityCode: values.cityCode || "",
                  stateCode: values.stateCode || "",
                  countryCode: addressCountryCode,
                } as OfficeAddress,
              ],
            }),
          });

          const result = await response.json();
          if (!response.ok || (result && result.error)) {
            const msg = result?.error || result?.message || "Failed to save data";
            showError(msg);
            throw new Error(msg);
          }

          showSuccess("Office updated successfully!");
          navigate(`${baseRoute}/brokerageFirm/${firmId}#offices`);
        } else {
          // CREATE office
          // const response = await fetch(`${process.env.REACT_APP_API_URL}/offices`, {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/organizations/${firmId}/offices`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              officeName: values.officeName || "",
              officeType: values.officeType || "",
              officeAddress: [
                {
                  // label: `${values.officeName}`,
                  label: "-",
                  addressLine1: values.streetAddress1 || "",
                  addressLine2: values.streetAddress2 || "",
                  city: values.city || "",
                  state: values.state || "",
                  country: addressCountry,
                  zip: values.zipcode || "",
                  phone: values.officePhone || "",
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                  cityCode: values.cityCode || "",
                  stateCode: values.stateCode || "",
                  countryCode: addressCountryCode,
                } as OfficeAddress,
              ],
              created_by: { id: firmId, name: "Brokerage Firm" },
              status: true,
            }),
          });

          const created = await response.json();
          const duplicateCandidates = Array.isArray(created?.duplicate_candidates)
            ? created.duplicate_candidates
                .map((item: any) => ({
                  resource_id: String(item?.resource_id || "").trim(),
                  resource_name: String(item?.resource_name || "Office").trim() || "Office",
                }))
                .filter((item: DuplicateOfficeCandidate) => item.resource_id)
            : [];
          if (
            created?.code === "DUPLICATE_OFFICE_CONFIRMATION_REQUIRED" ||
            duplicateCandidates.length > 0
          ) {
            pendingCreateBodyRef.current = {
              officeName: values.officeName || "",
              officeType: values.officeType || "",
              officeAddress: [
                {
                  label: "-",
                  addressLine1: values.streetAddress1 || "",
                  addressLine2: values.streetAddress2 || "",
                  city: values.city || "",
                  state: values.state || "",
                  country: addressCountry,
                  zip: values.zipcode || "",
                  phone: values.officePhone || "",
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                  cityCode: values.cityCode || "",
                  stateCode: values.stateCode || "",
                  countryCode: addressCountryCode,
                },
              ],
              created_by: { id: firmId, name: "Brokerage Firm" },
              status: true,
            };
            setDuplicateChoice("CREATE_NEW");
            setExistingOfficeId("");
            setDuplicatePrompt({
              candidates: duplicateCandidates,
              message:
                created?.error ||
                "An office with this address may already exist. Choose an existing office or confirm creating a new one.",
            });
            return;
          }
          if (!response.ok || created?.error) {
            const msg = created?.error || created?.message || "Failed to save data";
            showError(msg);
            throw new Error(msg);
          }

          // Create address entry (same as your Add flow)
          // try {
          //   const addressResponse = await fetch(
          //     `${process.env.REACT_APP_API_URL}/addresses/user/${created._id}`,
          //     {
          //       method: "POST",
          //       headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          //       body: JSON.stringify({
          //         // label: `${values.officeName} Address`,
          //         label: "-",
          //         first_name: values.officeName || "",
          //         last_name: "",
          //         company: "",
          //          user_type:"office",
          //         address_details: {
          //           // label: `${values.officeName} Address`,
          //           label: "-",
          //           addressLine1: values.streetAddress1 || "",
          //           addressLine2: values.streetAddress2 || "",
          //           city: values.city,
          //           state: values.state,
          //           zip: values.zipcode,
          //           country: addressCountry,
          //           phone: values.officePhone,
          //           cityCode: values.cityCode,
          //           stateCode: values.stateCode,
          //           countryCode: addressCountryCode,
          //           latitude: addressLatitude,
          //           longitude: addressLongitude,
          //         },
          //       }),
          //     }
          //   );

          //   const addr = await addressResponse.json();
          //   if (!addressResponse.ok || addr?.error) {
          //     const msg = addr?.error || addr?.message || "Failed to save data";
          //     showError(msg);
          //     throw new Error(msg);
          //   }
          // } catch (addressErr: any) {
          //   console.error(addressErr);
          //   showError(addressErr.message || "Failed to create address");
          //   // (Don’t throw here if you still want to navigate even when address creation fails)
          // }

          showSuccess("Office added successfully!");
          navigate(`${baseRoute}/brokerageFirm/${firmId}#offices`);
        }
      } catch (err: any) {
        console.error(err);
        showError(err?.message || "Something went wrong");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const closeDuplicatePrompt = () => {
    if (formSubmitting) return;
    setDuplicatePrompt(null);
    setExistingOfficeId("");
    setDuplicateChoice("CREATE_NEW");
  };

  const confirmDuplicateOffice = async () => {
    const baseBody = pendingCreateBodyRef.current;
    if (!baseBody || !firmId || !duplicatePrompt) return;
    if (duplicateChoice === "USE_EXISTING" && !existingOfficeId) {
      showError("Select an existing office.");
      return;
    }
    setFormSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/organizations/${firmId}/offices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...baseBody,
          duplicate_resolution: duplicateChoice,
          existing_office_id: duplicateChoice === "USE_EXISTING" ? existingOfficeId : "",
        }),
      });
      const created = await response.json();
      if (!response.ok || created?.error) {
        const msg = created?.error || created?.message || "Failed to save data";
        showError(msg);
        return;
      }
      showSuccess("Office added successfully!");
      setDuplicatePrompt(null);
      navigate(`${baseRoute}/brokerageFirm/${firmId}#offices`);
    } catch (err: any) {
      showError(err?.message || "Something went wrong");
    } finally {
      setFormSubmitting(false);
    }
  };

  const hasGoogleCoordinates =
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleStreetAddress1Change = (value: string) => {
    formik.setFieldValue("streetAddress1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };

  // ======= UI (kept to match your Add file's layout) =======
  return (
    <div className="content container">
      <div className="row">
        <div className="col-md-12 d-flex justify-content-center">
          <div className="col-md-9">
            <div className="mb-4">
              <div className="mt-1 mb-3">
                <h3>{isEdit ? "Edit Office" : "Add Office"}</h3>
              </div>
            </div>

            {/* card */}
            <div className="box-shadow shadow-border-3 mb-5">
              {/* header bar */}
              <div
                className="d-flex align-items-center ms-4"
                style={{ padding: "33px 18px", borderBottom: "1px solid #eef2f7" }}
              >
                <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 26, height: 26 }}>
                  <Building2 style={{ fontSize: 26, color: "#f54a00", lineHeight: 1 }} />
                </span>
                <div className="fs-19 roboto-new ms-2">Office Details</div>
              </div>

              {/* body */}
              <form onSubmit={formik.handleSubmit} className="mx-4 mb-4">
                <div style={{ padding: "54px 18px 0px 18px" }}>
                  <div className="ms-1 mb-2 fs-19 roboto-new">Office Information</div>
                  <hr />

                  <div className="row ms-1" id="row-overlap">
                    {/* Office Name */}
                    <div className="col-lg-8 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik as any}
                        name="officeName"
                        label="Office Name"
                        required
                        placeholder="Enter office name"
                      />
                    </div>

                    {/* Office Type */}
                    <div className="col-lg-4 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik as any}
                        name="officeType"
                        label="Office Type"
                        placeholder="Enter office type"
                      />
                    </div>

                    {/* Phone */}
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik as any}
                        name="officePhone"
                        label="Phone"
                        placeholder="(XXX) XXX-XXXX"
                      />
                    </div>

                    {/* Address Line 1 (Custom Autocomplete) */}
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <label className="form-label">
                        Address Line 1 <span className="text-danger">*</span>
                      </label>
                      <AddressAutocomplete
                        id="streetAddress1"
                        name="streetAddress1"
                        value={formik.values.streetAddress1}
                        onChange={handleStreetAddress1Change}
                        onSelect={(picked) => {
                          formik.setFieldValue("streetAddress1", picked.addressLine1);
                          formik.setFieldValue("city", picked.city);
                          formik.setFieldValue("cityCode", picked.cityCode);
                          formik.setFieldValue("state", picked.state);
                          formik.setFieldValue("stateCode", picked.stateCode);
                          formik.setFieldValue("zipcode", picked.zipcode);
                          formik.setFieldValue("country", picked.country);
                          formik.setFieldValue("countryCode", picked.countryCode);
                          formik.setFieldValue("latitude", picked.latitude);
                          formik.setFieldValue("longitude", picked.longitude);
                          formik.setFieldTouched("streetAddress1", false, false);
                          formik.setFieldError("streetAddress1", undefined);
                          clearAddressDetailErrors();
                        }}
                        className={`form-control ${formik.touched.streetAddress1 && formik.errors.streetAddress1 ? "is-invalid" : ""}`}
                        placeholder="Street address"
                        error={formik.touched.streetAddress1 && formik.errors.streetAddress1 ? (formik.errors.streetAddress1 as string) : undefined}
                        autoComplete="new-password"
                      />
                    </div>

                    {/* Address Line 2 */}
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik as any}
                        name="streetAddress2"
                        label="Address Line 2"
                        placeholder="Suite, unit, floor (optional)"
                      />
                    </div>

                    {/* Country / City */}
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInputDisable
                        formik={formik as any}
                        name="country"
                        label="Country"
                        required
                        placeholder="United States"
                        disabled
                        autocomplete="new-password"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInputDisable
                        formik={formik as any}
                        name="city"
                        label="City"
                        required
                        placeholder="Enter city"
                        disabled
                        autocomplete="new-password"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>

                    {/* State / Zip */}
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <label className="form-label">
                        State <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="state"
                        className="form-control"
                        placeholder="Select state"
                        value={formik.values.state}
                        disabled
                        autoComplete="new-password"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                      {formik.touched.state && formik.errors.state && (
                        <div className="invalid-feedback d-block">{formik.errors.state as string}</div>
                      )}
                    </div>
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInputDisable
                        formik={formik as any}
                        name="zipcode"
                        label="Zip Code"
                        required
                        placeholder="12345"
                        disabled
                        autocomplete="new-password"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 mb-2" id="row-overlap">
                  <hr className="ms-4 me-3" />
                </div>

                {/* footer */}
                <div className="d-flex justify-content-between align-items-center ms-1" style={{ padding: "14px 18px" }}>
                  <button type="button" onClick={() => navigate(-1)} className="btn back-btn surface-btn fw-bold fs-16">
                    <i className="bi bi-chevron-left me-2" />
                    Back
                  </button>

                  <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={formSubmitting}>
                    {formSubmitting ? "Submitting..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
            {/* /card */}
          </div>
        </div>
      </div>
      {duplicatePrompt ? (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
          onClick={closeDuplicatePrompt}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow roboto-new" style={{ borderRadius: 16 }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fs-19 mb-0">Office already exists</h5>
                <button type="button" className="btn-close" disabled={formSubmitting} onClick={closeDuplicatePrompt} />
              </div>
              <div className="modal-body pt-3">
                <p className="mb-3" style={{ color: "#4b5563", fontWeight: 400, fontSize: 15 }}>
                  {duplicatePrompt.message}
                </p>
                <div className="d-flex gap-2 mb-3">
                  {(
                    [
                      { value: "CREATE_NEW" as const, label: "Create new" },
                      { value: "USE_EXISTING" as const, label: "Existing" },
                    ]
                  ).map((choice) => {
                    const selected = duplicateChoice === choice.value;
                    return (
                      <button
                        key={choice.value}
                        type="button"
                        className="flex-fill text-start"
                        onClick={() => setDuplicateChoice(choice.value)}
                        style={{
                          border: selected ? "1px solid #155dfc" : "1px solid #e5e7eb",
                          background: selected ? "#eff6ff" : "#fff",
                          color: selected ? "#155dfc" : "#101828",
                          borderRadius: 12,
                          padding: "12px 14px",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      >
                        <span
                          className="d-inline-flex align-items-center justify-content-center me-2"
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: `2px solid ${selected ? "#155dfc" : "#d0d5dd"}`,
                            verticalAlign: "middle",
                          }}
                        >
                          {selected ? (
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#155dfc" }} />
                          ) : null}
                        </span>
                        {choice.label}
                      </button>
                    );
                  })}
                </div>
                {duplicateChoice === "USE_EXISTING" ? (
                  <div>
                    <label className="form-label mb-2">Office Name</label>
                    {duplicatePrompt.candidates.length === 0 ? (
                      <div style={{ color: "#667085", fontWeight: 400, fontSize: 15 }}>No existing offices were returned.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                        {duplicatePrompt.candidates.map((officeOption) => {
                          const selected = existingOfficeId === officeOption.resource_id;
                          return (
                            <button
                              key={officeOption.resource_id}
                              type="button"
                              className="text-start"
                              onClick={() => setExistingOfficeId(officeOption.resource_id)}
                              style={{
                                border: selected ? "1px solid #155dfc" : "1px solid #e5e7eb",
                                background: selected ? "#eff6ff" : "#fff",
                                color: "#101828",
                                borderRadius: 12,
                                padding: "12px 14px",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 15,
                                fontWeight: 500,
                              }}
                            >
                              {officeOption.resource_name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" disabled={formSubmitting} onClick={closeDuplicatePrompt}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={formSubmitting} onClick={() => void confirmDuplicateOffice()}>
                  {formSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SignageBrokerageOfficeForm;




























// import React, { useState, useRef } from "react";
// import { useLoadScript, Autocomplete } from "@react-google-maps/api";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import { showSuccess, showError } from "toast/toast";
// // import "react-toastify/dist/ReactToastify.css";
// import { useNavigate, useParams } from "react-router-dom";
// import { addressLine1Validation, cityValidation, countryValidation, officeNameValidation, phone2Validation, stateValidation, zipcodeValidation } from "validationSchemas/common";
// import { hash } from "crypto";

// const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// const SignageBrokerageAddOffice = () => {
//   const { firmId } = useParams<{ firmId: string }>();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string>("");
//   const [formSubmitting, setFormSubmitting] = useState(false);
//   const [Delete, setDelete] = useState(false);
//   const token = getAuthCookie("sessionToken");
//   const navigate = useNavigate();
//   const { isLoaded } = useLoadScript({
//     googleMapsApiKey: apiKey,
//     libraries: ["places"],
//   });

//   const [address, setAddress] = useState("");
//   const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

//   const formik = useFormik({
//     initialValues: {
//       officeName: "",
//       officeType: "",
//       contactName: "",
//       contactEmail: "",
//       contactPhone: "",
//       officePhone: "",
//       streetAddress1: "",
//       streetAddress2: "",
//       city: "",
//       state: "",
//       zipcode: "",
//       country: "",
//       cityCode: "",
//       stateCode: "",
//       countryCode: "",
//       latitude: 0,
//       longitude: 0,
//     },
//     validationSchema: Yup.object({
//       officeName: officeNameValidation,
//       officePhone: phone2Validation,
//       streetAddress1: addressLine1Validation,
//       city: cityValidation,
//       state: stateValidation,
//       zipcode: zipcodeValidation,
//       country: countryValidation,
//     }),
//     onSubmit: async (values) => {
//       console.log("Submitting form...", values);
//       setFormSubmitting(true);
//       try {
//         const response = await fetch(
//           `${process.env.REACT_APP_API_URL}/offices`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//             },
//             body: JSON.stringify({
//               officeName: values.officeName || "",
//               officeType: values.officeType || "",
//               officeAddress: [
//                 {
//                   label: `${values.officeName} Address`,
//                   addressLine1: values.streetAddress1 || "",
//                   addressLine2: values.streetAddress2 || "",
//                   city: values.city || "",
//                   state: values.state || "",
//                   country: values.country || "",
//                   zip: values.zipcode || "",
//                   phone: values.officePhone || "",
//                   cityCode: values.cityCode,
//                   stateCode: values.stateCode,
//                   countryCode: values.countryCode,
//                   latitude: values.latitude,
//                   longitude: values.longitude,
//                 },
//               ],
//               created_by: {
//                 id: firmId,
//                 name: "Brokerage Firm",
//               },
//               status: true,
//             }),
//           }
//         );

//         const result: any = await response.json();
//         if (!response.ok || result?.error) {
//           const message = result?.error || result?.message || "Failed to save data";
//           console.error("API Error:", message);
//           showError(message);
//           throw new Error(message);
//         }
//         try {
//           //                const addressResponse = await fetch(
//           //   `${process.env.REACT_APP_API_URL}/offices/${result._id}`,
//           //   {
//           //     method: "POST",
//           //     headers: {
//           //       "Content-Type": "application/json",
//           //       Authorization: `Bearer ${token}`,
//           //     },
//           //     body: JSON.stringify({
//           //       officeName: values.officeName || "",
//           //       officeType: values.officeType || "",
//           //       officeAddress: [
//           //         {
//           //           label: "brokerage Default Office Address",
//           //           addressLine1: values.streetAddress1 || "",
//           //           addressLine2:  values.streetAddress2 || "",
//           //           city: values.city || "",
//           //           state: values.state || "",
//           //           country: values.country || "",
//           //           zip: values.zipcode,
//           //           phone: values.officePhone,
//           //           cityCode: values.cityCode,
//           //           stateCode: values.stateCode,
//           //           countryCode: values.countryCode,
//           //           latitude: values.latitude,
//           //           longitude: values.longitude,
//           //         }
//           //       ],
//           //     }),
//           //   }
//           // );
//           const addressResponse = await fetch(
//             `${process.env.REACT_APP_API_URL}/addresses/user/${result._id}`,
//             {
//               method: "POST",
//               headers: {
//                 "Content-Type": "application/json",
//                 Authorization: `Bearer ${token}`,
//               },
//               body: JSON.stringify({
//                 // user_id: userId,
//                 label: `${values.officeName} Address`,
//                 first_name: values.officeName || "",
//                 last_name: "",
//                 company: "",
//                 address_details: {
//                   label: `${values.officeName} Address`,
//                   addressLine1: values.streetAddress1 || "",
//                   addressLine2: values.streetAddress2 || "",
//                   city: values.city,
//                   state: values.state,
//                   zip: values.zipcode,
//                   country: values.country,
//                   phone: values.officePhone,
//                   cityCode: values.cityCode,
//                   stateCode: values.stateCode,
//                   countryCode: values.countryCode,
//                   latitude: values.latitude,
//                   longitude: values.longitude,
//                 },
//               }),
//             }
//           );
//           const data: any = await addressResponse.json();

//           // ✅ Check if API failed
//           if (!addressResponse.ok || data?.error) {
//             const message = data?.error || data?.message || "Failed to save data";
//             console.error("API Error:", message);
//             showError(message);
//             throw new Error(message); // stop further code
//           }
//           showSuccess("Office added successfully!");
//           // showSuccess("Office Address added successfully!");
//           navigate(`${baseRoute}/brokerageFirm/${firmId}#offices`);

//         } catch (addressErr: any) {
//           console.error(addressErr);
//           showError(addressErr.message);
//         }
//       } catch (err) {
//         console.error(err instanceof Error ? err.message : "Unknown error");
//       } finally {
//         setFormSubmitting(false);
//       }
//     },
//   });

//   const handlePlaceChanged = () => {
//     if (autocompleteRef.current) {
//       const place = autocompleteRef.current.getPlace();
//       if (!place || !place.address_components) {
//         showError("Please select a valid address from the suggestions.");
//         return;
//       }
//       // console.log("Place changed:", place);
//       const addressComponents = place.address_components;
//       if (addressComponents) {
//         const getComponent = (type: string) => {
//           const component = addressComponents?.find(c => c.types.includes(type));
//           return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
//         };

//         const streetNumber = getComponent('street_number').long_name;
//         const route = getComponent('route').long_name;

//         const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
//         const stateObj = getComponent('administrative_area_level_1');
//         const postalCode = getComponent('postal_code').long_name;
//         const countryObj = getComponent('country');


//         const street = `${streetNumber} ${route}`.trim();

//         const latitude = place.geometry?.location?.lat();
//         const longitude = place.geometry?.location?.lng();

//         formik.setFieldValue('streetAddress1', street);
//         formik.setFieldValue('city', cityObj.long_name);
//         formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
//         formik.setFieldValue('state', stateObj.long_name);
//         formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
//         formik.setFieldValue('zipcode', postalCode);
//         formik.setFieldValue('country', countryObj.long_name);
//         formik.setFieldValue('countryCode', countryObj.short_name);

//         if (latitude && longitude) {
//           formik.setFieldValue('latitude', latitude);
//           formik.setFieldValue('longitude', longitude);
//         }
//         // :white_tick: ZIP code logic with fallback
//         if (postalCode) {
//           formik.setFieldValue("zipcode", postalCode);
//         } else if (latitude && longitude) {
//           // Fallback to reverse geocoding
//           fetch(
//             `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
//           )
//             .then((res) => res.json())
//             .then((data) => {
//               const postalComp = data.results[0]?.address_components.find((c: any) =>
//                 c.types.includes("postal_code")
//               );
//               if (postalComp) {
//                 console.log("Postal Code from Reverse Geocoding:", postalComp.long_name);
//                 formik.setFieldValue("zipcode", postalComp.long_name);
//               } else {
//                 console.warn("No postal_code found even from reverse geocoding");
//               }
//             })
//             .catch((err) => console.error("Reverse geocoding error:", err));
//         }




//       }
//     }
//   };

//   if (!isLoaded) return <div>Loading...</div>;

//   return (
//     <div className="content">
//       <div className="page-container mt-3 col-md-9">
//         <h3 className="m-0 text-center fw-bold addheaders">Add Brokerage Office</h3>
//       </div>
//       {/* <h2 className="d-flex justify-content-center">Add Office </h2> */}
//       <div className="row ms-0">
//         <div className="col-md-12" id="row-overlap">
//           <div className="col-md-9">
//             <div className="addheaders-content mt-1 card-bg-fill">
//               <div className="p-2 px-4 mt-1">
//                 <form onSubmit={formik.handleSubmit}>
//                   <div className="row">
//                     <div className="col-md-4 mb-3">
//                       <label className="form-label">
//                         Office Name <span className="text-danger">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="officeName"
//                         name="officeName"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.officeName}
//                       />
//                       {formik.touched.officeName &&
//                         formik.errors.officeName ? (
//                         <div className="text-danger">
//                           {formik.errors.officeName}
//                         </div>
//                       ) : null}
//                     </div>

//                     <div className="col-md-4 mb-3">
//                       <label className="form-label">
//                         Office Phone Number
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="officePhone"
//                         name="officePhone"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.officePhone}
//                       />
//                       {formik.touched.officePhone &&
//                         formik.errors.officePhone ? (
//                         <div className="text-danger">
//                           {formik.errors.officePhone}
//                         </div>
//                       ) : null}
//                     </div>

//                     <div className="col-md-4 mb-3">
//                       <label className="form-label">
//                         Street Address 1{" "}
//                         <span className="text-danger">*</span>
//                       </label>
//                       {
//                         isLoaded &&
//                         <Autocomplete
//                           onLoad={(autocomplete) =>
//                             (autocompleteRef.current = autocomplete)
//                           }
//                           onPlaceChanged={handlePlaceChanged}
//                         >
//                           <input
//                             type="text"
//                             className="form-control"
//                             id="streetAddress1"
//                             name="streetAddress1"
//                             onChange={formik.handleChange}
//                             onBlur={formik.handleBlur}
//                             value={formik.values.streetAddress1}
//                           />
//                         </Autocomplete>
//                       }
//                       {formik.touched.streetAddress1 &&
//                         formik.errors.streetAddress1 ? (
//                         <div className="text-danger">
//                           {formik.errors.streetAddress1}
//                         </div>
//                       ) : null}
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <label className="form-label">
//                         Street Address 2{" "}
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="streetAddress2"
//                         name="streetAddress2"
//                         placeholder="Enter Address line 2"
//                       />
//                       {/* <div className="invalid-feedback">
//                             Please enter Address line 2
//                           </div> */}
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <label className="form-label">Country</label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="country"
//                         name="country"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.country}
//                       />
//                       {formik.touched.country && formik.errors.country ? (
//                         <div className="text-danger">
//                           {formik.errors.country}
//                         </div>
//                       ) : null}
//                     </div>
//                     <div className="col-md-3 mb-3">
//                       <label className="form-label">
//                         State <span className="text-danger">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="state"
//                         name="state"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.state}
//                       />
//                       {formik.touched.state && formik.errors.state ? (
//                         <div className="text-danger">
//                           {formik.errors.state}
//                         </div>
//                       ) : null}
//                     </div>
//                     <div className="col-md-3 mb-3">
//                       <label className="form-label">
//                         City <span className="text-danger">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="city"
//                         name="city"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.city}
//                       />
//                       {formik.touched.city && formik.errors.city ? (
//                         <div className="text-danger">
//                           {formik.errors.city}
//                         </div>
//                       ) : null}
//                     </div>
//                     <div className="col-md-3 mb-3">
//                       <label className="form-label">
//                         Zip code <span className="text-danger">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         id="zipcode"
//                         name="zipcode"
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.zipcode}
//                       />
//                       {formik.touched.zipcode && formik.errors.zipcode ? (
//                         <div className="text-danger">
//                           {formik.errors.zipcode}
//                         </div>
//                       ) : null}
//                     </div>
//                     <div className="row mb-3 rcust-margin-4">
//                       <div className="col-md-12 d-flex justify-content-between">
//                         <button
//                           className="btn btn-outline-secondary br-8 backbutton"
//                           type="button"
//                           onClick={() => navigate(-1)}
//                         >
//                           Back
//                         </button>

//                         <button className="btn btn-primary submit-btn" type="submit" disabled={formSubmitting}>
//                           Save
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SignageBrokerageAddOffice;
